/**
 * The three calls a shared enquiry form makes, and the only three in the app
 * that carry no bearer token.
 *
 * `api.get`/`api.post` only set `Authorization` when a token is passed, so a
 * public call is simply one made without one. Nothing here may reach for
 * `getToken()`: there is no session on this screen, and no workspace behind it
 * — the practitioner's own name in the path is what tells the backend whose
 * form this is.
 *
 * snake_case stops here, as it does in every other service: the screens above
 * see `PublicEnquiryForm` and `PublicEnquiryAnswers` in the app's own casing.
 */

import { api, API_ENDPOINTS, withValidationDetail } from "../../../lib/api";
import { ApiError } from "../../../lib/http";
import { stateOfCity } from "../../../lib/locations";
import { FORMS_PREVIEW } from "../../../lib/site/config";
import {
  PREVIEW_DUPLICATE_EMAIL,
  PREVIEW_DUPLICATE_PHONE,
} from "../data/publicEnquiryPreview";
// A deep import rather than the brand barrel: that barrel pulls in the whole
// My brand screen, and this needs one function out of it — the one that turns
// a stored file's path into a URL rooted at the API's origin rather than at
// the page's.
import { mediaUrl } from "../../brand/services/fileService";
import { toEnumConstant } from "../../clients/clientPayload";
import type { PublicEnquiryField } from "../publicEnquiryValidation";
import type {
  PublicBrand,
  PublicContactCheckResponse,
  PublicWorkspaceResponse,
  PublicEnquiryAnswers,
  PublicEnquiryForm,
  PublicEnquiryFormResponse,
  PublicEnquiryPayload,
  PublicGoalOption,
  PublicServiceResponse,
} from "../types/enquiries.types";

/**
 * What the page calls the practitioner.
 *
 * Their brand name where they have set one, their own name where they have
 * not, and an empty string where the workspace has neither — which the shell
 * above reads as "draw the form's own title and no lockup". Never a fallback
 * to Naurist: the person reading this is answering their dietitian.
 */
const brandNameOf = (response: PublicWorkspaceResponse): string =>
  response.brand_name?.trim() || response.contact_name?.trim() || "";

const toBrand = (response: PublicWorkspaceResponse): PublicBrand => ({
  name: brandNameOf(response),
  // The backend answers with a path rooted at its own origin, and this page is
  // served from another host entirely — so the URL has to carry the API's
  // origin with it. `mediaUrl` is what resolves the three shapes the field
  // arrives in; concatenating would break two of them.
  logoUrl: response.logo ? mediaUrl(response.logo.url) : "",
});

/**
 * What the Goal dropdown offers, out of the services served with the form.
 *
 * A row with no name or no id is dropped, for the reason every other list here
 * drops one: an option nobody could pick, or one that could be picked and then
 * not sent, is worse than an option that was never offered. So is a service
 * the practitioner has taken down — the endpoint filters those out already,
 * and this is the second line.
 */
const toGoalOptions = (
  services: PublicServiceResponse[] | null | undefined
): PublicGoalOption[] =>
  (services ?? [])
    .filter(
      (row) => row?.name?.trim() && row.public_id && row.is_active !== false
    )
    .map((row) => ({ id: row.public_id, name: row.name.trim() }));

/**
 * The whole response, in the app's own shape.
 *
 * `workspace` is the backend's key and `brand` is what every screen above
 * calls it — the rename happens here, which is the point of the boundary. The
 * object is defaulted rather than assumed, so a response that arrived without
 * it draws the form under its own title instead of throwing on `.brand_name`
 * and losing the two catalogs with it.
 */
const toForm = (response: PublicEnquiryFormResponse): PublicEnquiryForm => ({
  brand: toBrand(response.workspace ?? { brand_name: "", logo: null }),
  goals: toGoalOptions(response.services),
  // Rows with no usable name are dropped rather than shown: an empty option is
  // a dead line in a dropdown, and one nobody could ever pick.
  lifeStyles: (response.life_styles ?? []).filter((row) => row?.name?.trim()),
});

/**
 * The form behind one practitioner's link: who they are, and what their form
 * offers.
 *
 * A name nobody has claimed — or one whose owner has switched sharing off —
 * comes back 404. That reaches the caller as an `ApiError` carrying the status
 * rather than as an empty form, so the screen can tell "this link is not
 * available" from "we could not reach the server".
 */
export async function fetchPublicEnquiryForm(
  handle: string,
  signal?: AbortSignal
): Promise<PublicEnquiryForm> {
  const response = await api.get<PublicEnquiryFormResponse>(
    API_ENDPOINTS.enquiries.publicForm(handle),
    { signal }
  );

  return toForm(response);
}

/* ── Is this contact already on file? ─────────────────────────────────── */

/** Which of the two contact fields is being asked about. */
export type PublicContactField = "email" | "phone";

/** The query parameter each one goes up as — the form's name, then the wire's. */
const CONTACT_PARAM: Record<PublicContactField, string> = {
  email: "email",
  phone: "phone_number",
};

/**
 * What the field says when the server said "yes" but did not say why.
 *
 * Per field rather than one line for both, because the message appears under
 * one control and has to read as being about that control.
 */
const ALREADY_REGISTERED: Record<PublicContactField, string> = {
  email: "You have already sent an enquiry with this email.",
  phone: "An enquiry from this number already exists.",
};

/**
 * Whether this practitioner already holds an enquiry from one contact detail.
 *
 * The message to show, or `""` for a contact that is new — a string rather
 * than a boolean because the server writes its own words and those are what
 * the field shows; the fallback above only covers a `true` that arrived
 * without any.
 *
 * One field per call. The two are asked about separately so each answer can
 * land under the control it is about, which is the whole point of doing this
 * before the submit rather than after.
 */
export async function checkPublicContact(
  handle: string,
  field: PublicContactField,
  value: string,
  signal?: AbortSignal
): Promise<string> {
  // Preview builds have no endpoint to ask, the same as the read and the
  // submit. The two answers that fail a preview submit fail this too, so the
  // inline message can be looked at without a server.
  if (FORMS_PREVIEW) {
    const taken =
      (field === "email" && value === PREVIEW_DUPLICATE_EMAIL) ||
      (field === "phone" && value === PREVIEW_DUPLICATE_PHONE);
    return taken ? ALREADY_REGISTERED[field] : "";
  }

  const response = await api.get<PublicContactCheckResponse>(
    API_ENDPOINTS.enquiries.publicCheckContact(handle),
    { query: { [CONTACT_PARAM[field]]: value }, signal }
  );

  if (!response?.already_registered) return "";
  return response.message?.trim() || ALREADY_REGISTERED[field];
}

/** The id behind a displayed name, or `null` when the list has no such row. */
const idOf = <T extends { id: number | string; name: string }>(
  options: T[],
  name: string
): T["id"] | null =>
  options.find((option) => option.name === name)?.id ?? null;

/**
 * Turns the answers into the body the endpoint takes.
 *
 * The dropdowns display names and the API takes the ids its catalog lists
 * carry, so the lists served with the form are what resolve one to the other.
 * A name that no longer matches a row means the catalog moved while the form
 * sat open — that fails the whole build rather than sending `null`, because an
 * enquiry saved against the wrong goal is worse than one refused.
 */
export const toPublicEnquiryPayload = (
  answers: PublicEnquiryAnswers,
  form: PublicEnquiryForm
): PublicEnquiryPayload | null => {
  // Every goal picked, each resolved on its own — the same rule the dashboard
  // form's `enquiryPayload` applies. One name that no longer matches a row
  // fails the whole build rather than being quietly dropped. What it resolves
  // to is a service's `public_id`, since that is what the form was served.
  const goalIds = answers.goals.map((name) =>
    idOf<PublicGoalOption>(form.goals, name)
  );
  const lifeStyleId = idOf(form.lifeStyles, answers.lifestyle);

  if (goalIds.some((id) => id === null) || lifeStyleId === null) return null;

  return {
    first_name: answers.firstName.trim(),
    last_name: answers.lastName.trim(),
    // Always a number by the time this runs: the box takes digits only and the
    // form will not send until one is in it.
    age: Number(answers.age),
    gender: toEnumConstant(answers.gender),
    goals: goalIds as string[],
    life_style: lifeStyleId,
    email: answers.email.trim(),
    phone_number: answers.phone,
    city: answers.city,
    // Never asked for: the city is enough to know it, and that is one fewer
    // dropdown on a form a stranger is deciding whether to bother with.
    //
    // Empty where the city was typed rather than picked — the field is
    // creatable, so it accepts towns the dataset has never heard of, and this
    // will not invent a state for one. The practitioner can fill it in against
    // the enquiry; refusing the submission over it would be a worse trade.
    state: stateOfCity(answers.city),
    enquiry_source: "FORM",
  };
};

/**
 * The backend's field names, in the form's own.
 *
 * Only the fields this form actually draws: a complaint about something it
 * never asked for has nowhere to be shown and belongs in the message above the
 * button instead.
 */
const FIELD_BY_WIRE_NAME: Record<string, PublicEnquiryField> = {
  first_name: "firstName",
  last_name: "lastName",
  age: "age",
  gender: "gender",
  goals: "goals",
  life_style: "lifestyle",
  email: "email",
  phone_number: "phone",
  city: "city",
};

/** The first usable line out of whatever DRF put against a field. */
const messageOf = (value: unknown): string => {
  const text = Array.isArray(value) ? value[0] : value;
  return typeof text === "string" ? text.trim() : "";
};

/**
 * The server's per-field complaints, keyed by the control they belong under.
 *
 * This is what puts "somebody has already enquired with this email address"
 * inside the Email field rather than in a banner over the whole form. A
 * duplicate is a problem with one answer, and the person reading has to be
 * shown *which* — a message at the top of a nine-field form makes them hunt.
 *
 * Two envelopes are read, because the backend may send either: this API's own
 * `{ details: { field: [...] } }`, and plain DRF's `{ field: [...] }`. Anything
 * else — a bare `detail`, a 500, a string — returns nothing here and is left to
 * the caller to show as one message.
 *
 * This stays the last word even though `checkPublicContact` above now asks
 * the same question while the form is being filled in. The live check is a
 * courtesy — it saves somebody filling in nine fields to be told at the end
 * that they had already written in — and it is not a guarantee: an enquiry can
 * arrive between the check and the submit, and a browser can skip the check
 * altogether. The server refusing the `POST` is what actually holds, and this
 * is what shows that refusal.
 */
export const toFieldErrors = (
  error: unknown
): Partial<Record<PublicEnquiryField, string>> => {
  if (!(error instanceof ApiError) || !error.data) return {};

  const body = error.data as Record<string, unknown>;
  const details =
    body.details && typeof body.details === "object"
      ? (body.details as Record<string, unknown>)
      : body;

  const errors: Partial<Record<PublicEnquiryField, string>> = {};
  Object.entries(details).forEach(([wireName, value]) => {
    const field = FIELD_BY_WIRE_NAME[wireName];
    const message = messageOf(value);
    if (field && message) errors[field] = message;
  });

  return errors;
};

/**
 * Raises the enquiry.
 *
 * What comes back is not read and not shown. The record belongs to the
 * practitioner's workspace; the person who filled the form gets a thank-you
 * and nothing else — no id, no copy of their answers, nothing to return to.
 *
 * A 400 arrives as an `ApiError` whose message names the field the backend
 * rejected rather than just "validation failed"; a 404 means the link stopped
 * working between the form being opened and being sent.
 */
export async function submitPublicEnquiry(
  handle: string,
  payload: PublicEnquiryPayload,
  signal?: AbortSignal
): Promise<void> {
  // Preview builds have no endpoint to post to — the same reason the read is
  // stubbed. Without this the thank-you screen would be unreachable, since
  // every submit would end on the failure message instead. Two answers fail on
  // purpose, so the duplicate states can be looked at too.
  if (FORMS_PREVIEW) {
    const duplicate =
      payload.email === PREVIEW_DUPLICATE_EMAIL
        ? { email: ["You have already sent an enquiry with this email."] }
        : payload.phone_number === PREVIEW_DUPLICATE_PHONE
        ? { phone_number: ["An enquiry from this number already exists."] }
        : null;

    if (duplicate) {
      throw new ApiError("Validation failed", 400, { details: duplicate });
    }
    return;
  }

  try {
    await api.post<unknown>(
      API_ENDPOINTS.enquiries.publicSubmit(handle),
      payload,
      { signal }
    );
  } catch (error) {
    throw withValidationDetail(error);
  }
}
